import { useMutation, useQuery } from "@apollo/client/react";
import { useMemo } from 'react';
import { CCRewardStatus } from '../enums/shared.enums';
import {
    GET_REFERRAL_CODE,
    GET_CARE_CIRCLE_STATUS,
    GET_CC_REFERRAL_PROGRESS,
    GET_REFERRAL_PROGRAM,
} from '../graphql/resolvers/queries';
import {
    GENERATE_REFERRAL_CODE,
    USE_REFERRAL_REWARD,
} from '../graphql/resolvers/mutations';
import {
    CareCircleStatusResponse,
    GenerateReferralCodeResponse,
    GetCCReferralProgressResponse,
    GetReferralCodeResponse,
    ReferralProgramResponse,
    UseReferralRewardResponse,
} from '../@types/referral.types';
import { useSnackbar } from 'notistack';

type ReferralHookQueries =
    | 'GET_REFERRAL_CODE'
    | 'GET_CARE_CIRCLE_STATUS'
    | 'GET_CC_REFERRAL_PROGRESS'
    | 'GET_REFERRAL_PROGRAM';

interface CCReferralHookOptions {
    queriesToRun?: ReferralHookQueries[];
}

export default function useCCReferral(options: CCReferralHookOptions = {}) {
    const { enqueueSnackbar } = useSnackbar()
    // The caller's stable referral code (null until enrolled).
    const {
        data: referralCodeData,
        loading: referralCodeLoading,
        error: referralCodeError,
        refetch: refetchReferralCode,
    } = useQuery<GetReferralCodeResponse>(GET_REFERRAL_CODE, {
        skip: !options?.queriesToRun?.includes('GET_REFERRAL_CODE'),
    });

    // Care Circle enrollment status.
    const {
        data: statusData,
        loading: statusLoading,
        error: statusError,
        refetch: refetchStatus,
    } = useQuery<CareCircleStatusResponse>(GET_CARE_CIRCLE_STATUS, {
        skip: !options?.queriesToRun?.includes('GET_CARE_CIRCLE_STATUS'),
    });

    // Reward-level progress (In Progress + Completed tabs).
    const {
        data: progressData,
        loading: progressLoading,
        error: progressError,
        refetch: refetchProgress,
    } = useQuery<GetCCReferralProgressResponse>(GET_CC_REFERRAL_PROGRESS, {
        skip: !options?.queriesToRun?.includes('GET_CC_REFERRAL_PROGRESS'),
    });

    // Static program constants (12 levels, 3 referrals/reward, 1 month).
    const {
        data: programData,
        loading: programLoading,
        error: programError,
    } = useQuery<ReferralProgramResponse>(GET_REFERRAL_PROGRAM, {
        skip: !options?.queriesToRun?.includes('GET_REFERRAL_PROGRAM'),
    });

    // Enroll and generate the stable referral code (idempotent).
    const [generateReferralCode, { loading: generateLoading }] =
        useMutation<GenerateReferralCodeResponse>(GENERATE_REFERRAL_CODE, {
            refetchQueries: [
                { query: GET_REFERRAL_CODE },
                { query: GET_CARE_CIRCLE_STATUS },
            ],
            awaitRefetchQueries: true,
        });

    // Redeem a completed reward.
    const [useReferralReward, { loading: redeemLoading }] = useMutation<
        UseReferralRewardResponse,
        { ccReferralProgressId: string }
    >(USE_REFERRAL_REWARD, {
        refetchQueries: [{ query: GET_CC_REFERRAL_PROGRESS }],
        awaitRefetchQueries: true,
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: "error" })
        }
    },);

    const { inProgressRewards, completedRewards } = useMemo(() => {
        const progress = progressData?.getCCReferralProgress ?? [];
        return {
            inProgressRewards: progress.filter(
                (reward) => reward.status === CCRewardStatus.IN_PROGRESS
            ),
            // COMPLETED and REDEEMED both surface under the Completed tab.
            completedRewards: progress.filter(
                (reward) => reward.status !== CCRewardStatus.IN_PROGRESS
            ),
        };
    }, [progressData]);

    return {
        // Referral code
        referralCode: referralCodeData?.getReferralCode ?? null,
        referralCodeLoading,
        referralCodeError,
        refetchReferralCode,

        // Enrollment status
        careCircleStatus: statusData?.careCircleStatus ?? null,
        enrolled: statusData?.careCircleStatus?.enrolled ?? false,
        statusLoading,
        statusError,
        refetchStatus,

        // Reward progress
        rewards: progressData?.getCCReferralProgress ?? [],
        inProgressRewards,
        completedRewards,
        progressLoading,
        progressError,
        refetchProgress,

        // Program constants
        referralProgram: programData?.referralProgram ?? null,
        programLoading,
        programError,

        // Mutations
        generateReferralCode,
        generateLoading,
        redeemReferralReward: useReferralReward,
        redeemLoading,
    };
}
